import React, { ChangeEvent } from 'react';
import type { GlueIntegration, IntegrationAccess, NodeInstance } from '@glueapp/graph-types';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { IntegrationAuthorizer } from './integrationAuthorization/IntegrationAuthorizer';
import { GraphReducerActions, setNodeSelectedIntegrationAccess } from '../GraphEditor/actions';

interface Props {
    nodeInstance: NodeInstance;
    integration: GlueIntegration<any>;
    integrationAccesses: IntegrationAccess[];
    deleteIntegrationAccess: (integrationAccess: IntegrationAccess) => void;
    dispatchGraphUpdate: (action: GraphReducerActions) => void;
}

export const IntegrationEditor: React.FC<Props> = ({
    nodeInstance,
    integration,
    integrationAccesses,
    deleteIntegrationAccess,
    dispatchGraphUpdate,
}) => {
    const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedAccess = integrationAccesses.find(ia => ia.id === event.target.value);
        if (selectedAccess) {
            dispatchGraphUpdate(setNodeSelectedIntegrationAccess(nodeInstance.id, selectedAccess));
        }
    };

    const renderIntegrationSelect = () => {
        if (integrationAccesses.length) {
            const selectedIntegrationAccess = nodeInstance.selectedIntegrationAccess ?? integrationAccesses[0];
            return (
                <div className="flex flex-col gap-2">
                    <Select
                        disabled={selectedIntegrationAccess === undefined}
                        value={selectedIntegrationAccess.id}
                        onValueChange={value => handleSelect({ target: { value } } as ChangeEvent<HTMLSelectElement>)}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {integrationAccesses.map(ia => (
                                <SelectItem value={ia.id} key={ia.id}>
                                    {integration.renderOptionLabel(ia.metaData)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => deleteIntegrationAccess(selectedIntegrationAccess)}>
                        Remove
                    </Button>
                </div>
            );
        }
    };

    return (
        <div className="flex flex-col gap-2">
            {renderIntegrationSelect()}
            <IntegrationAuthorizer
                integration={integration}
                label={integrationAccesses.length === 0 ? 'Authorize' : 'Authorize another Account'}
            />
        </div>
    );
};
